import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus, X, RefreshCw, Pencil } from 'lucide-react';
import { loadLdSettings } from '../../utils/dateRange';
import AudienceRuleModal from './AudienceRuleModal';
import {
  RULE_META, normalizeRules, isRuleIncomplete, audienceRulesPayload, describeRule,
} from './audienceRuleMeta';

/**
 * Expected-attendee rules for a training session (docs/modules/ld.md §3.2):
 * the list of rules on the form, each with the head count it contributes,
 * and the people they add up to. A rule is put together in its own pop-up
 * (AudienceRuleModal) and lands here once it is complete.
 *
 * Rules add up: anyone matching any one of them is expected. Narrowing is
 * done inside a rule (department AND grade AND designation) or by striking
 * people out of the preview, which writes them into rules.exclude so
 * Regenerate keeps honouring it.
 *
 * The preview endpoint is stateless because the New form needs a head count
 * before the session, and its id, exists.
 *
 * Props:
 *   value        — rule set ({include, exclude}) or null
 *   onChange     — (rules) => void
 *   lookupOptions— { staff_department: [], staff_designation: [] }
 *   staffOptions — [{ id, name, position }] for the individual picker
 *   categoryOptions / qualTypeOptions — [{ id, title }]
 *   sessionDate  — the session's own date; "missed session" may only look back
 *   sessionId    — the session being edited (null when creating); it is kept
 *                  out of its own "missed session" picker
 *   trainerStaffIds — the session's trainers, dropped from the preview as
 *                  the save drops them
 *   fetchJSON    — the page's fetch helper
 */
const AudienceRulesEditor = ({
  value, onChange, lookupOptions = {}, staffOptions = [],
  categoryOptions = [], qualTypeOptions = [], sessionDate = null, sessionId = null,
  trainerStaffIds = [], fetchJSON,
}) => {
  const rules = useMemo(() => normalizeRules(value), [value]);
  const trainerKey = JSON.stringify(trainerStaffIds || []);

  // null | { index: null } (adding) | { index: n } (editing rule n)
  const [modal, setModal] = useState(null);
  const [pastSessions, setPastSessions] = useState([]);
  const [preview, setPreview] = useState({ loading: false, count: null, data: [], error: null });
  const [expanded, setExpanded] = useState(false);

  // The 40-hour target and training-year dates behind the dated rules.
  useEffect(() => { loadLdSettings(); }, []);

  // Only sessions that already happened may be referenced (a rule reading a
  // later session's attendance would have nothing to read, and it is how a
  // rule loop would start). The session being edited is excluded server-side:
  // a session offering itself is the tightest loop of all. Loaded while a
  // missed-session rule is on the list or being built, and refetched when
  // the date or the excluded id changes.
  const needsPastSessions = modal !== null || rules.include.some((r) => r.type === 'missed_session');
  useEffect(() => {
    if (!needsPastSessions) return undefined;
    const qs = new URLSearchParams();
    if (sessionDate) qs.set('before', sessionDate);
    if (sessionId) qs.set('exclude', String(sessionId));
    const suffix = qs.toString() ? `?${qs}` : '';
    let cancelled = false;
    fetchJSON(`/api/ld/sessions/options/past${suffix}`)
      .then((d) => { if (!cancelled) setPastSessions(d.data || []); })
      .catch(() => { if (!cancelled) setPastSessions([]); });
    return () => { cancelled = true; };
  }, [needsPastSessions, sessionId, sessionDate, fetchJSON]);

  const payloadKey = JSON.stringify(audienceRulesPayload(rules));

  // Debounced so a burst of edits does not fire a request each.
  useEffect(() => {
    const body = JSON.parse(payloadKey);
    if (body.include.length === 0) {
      setPreview({ loading: false, count: 0, data: [], error: null });
      return undefined;
    }
    let cancelled = false;
    setPreview((p) => ({ ...p, loading: true, error: null }));
    const timer = setTimeout(() => {
      fetchJSON('/api/ld/roster/preview', {
        method: 'POST',
        // The trainers are dropped server-side, so the count on screen is
        // the count the save will materialise even when a rule matches them.
        body: JSON.stringify({ rules: body, trainerStaffIds: JSON.parse(trainerKey) }),
      })
        .then((d) => {
          if (!cancelled) setPreview({ loading: false, count: d.count, data: d.data || [], error: null });
        })
        .catch((e) => {
          if (!cancelled) setPreview({ loading: false, count: null, data: [], error: e.message });
        });
    }, 400);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [payloadKey, trainerKey, fetchJSON]);

  const setRules = useCallback((next) => onChange(next), [onChange]);

  const saveRule = (rule) => {
    const include = modal.index === null
      ? [...rules.include, rule]
      : rules.include.map((r, i) => (i === modal.index ? rule : r));
    setModal(null);
    setRules({ ...rules, include });
  };
  const removeRule = (index) => setRules({
    ...rules,
    include: rules.include.filter((_, i) => i !== index),
  });
  const exclude = (staffId) => setRules({
    ...rules,
    exclude: { staffIds: [...rules.exclude.staffIds, Number(staffId)] },
  });
  const unexclude = (staffId) => setRules({
    ...rules,
    exclude: { staffIds: rules.exclude.staffIds.filter((id) => Number(id) !== Number(staffId)) },
  });

  const staffName = (id) => staffOptions.find((s) => String(s.id) === String(id))?.name || `#${id}`;

  // Names for the ids a rule carries, so its row reads "Not yet taken:
  // Ethics" rather than "#4".
  const lookups = useMemo(() => ({
    sessions: Object.fromEntries(pastSessions.map((s) => [s.id, s])),
    categories: Object.fromEntries(categoryOptions.map((c) => [c.id, c.title])),
    qualificationTypes: Object.fromEntries(qualTypeOptions.map((q) => [q.id, q.title])),
    staff: Object.fromEntries(staffOptions.map((s) => [s.id, s.name])),
  }), [pastSessions, categoryOptions, qualTypeOptions, staffOptions]);

  // How many of the previewed people each rule reaches. Incomplete rules are
  // not sent, so the preview's rule indexes count complete rules only; map
  // them back onto the list's own positions.
  const countByRule = useMemo(() => {
    const sentIndex = [];
    rules.include.forEach((r, i) => { if (!isRuleIncomplete(r)) sentIndex.push(i); });
    const counts = rules.include.map(() => 0);
    preview.data.forEach((p) => {
      (p.matchedRules || [p.ruleIndex]).forEach((k) => {
        const i = sentIndex[k];
        if (i !== undefined) counts[i] += 1;
      });
    });
    return counts;
  }, [rules.include, preview.data]);

  const usedTypes = useMemo(() => new Set(rules.include.map((r) => r.type)), [rules.include]);
  const hasRules = rules.include.length > 0;

  return (
    <div className="border border-slate-300 rounded-lg bg-slate-50 p-3 space-y-3">
      {/* ── Rules ─────────────────────────────────────────────────────────── */}
      {hasRules && (
        <ul className="bg-white border border-slate-200 rounded-lg divide-y divide-slate-100">
          {rules.include.map((rule, index) => {
            const meta = RULE_META[rule.type];
            const Icon = meta?.icon;
            const incomplete = isRuleIncomplete(rule);
            return (
              <li key={index} className="flex items-center gap-2.5 px-3 py-2">
                {Icon && <Icon className="w-4 h-4 text-slate-400 shrink-0" />}
                <span className="min-w-0 flex-1">
                  <span className="block text-sm text-slate-800 truncate" title={describeRule(rule, lookups)}>
                    {describeRule(rule, lookups)}
                  </span>
                  <span className="block text-xs text-slate-500 truncate">{meta?.label || rule.type}</span>
                </span>
                <span
                  className={`shrink-0 px-2 py-0.5 rounded text-xs tabular-nums ${
                    incomplete ? 'bg-amber-50 text-amber-700'
                      : countByRule[index] === 0 && !preview.loading ? 'bg-amber-50 text-amber-700'
                        : 'bg-slate-100 text-slate-600'}`}
                  title={incomplete ? meta?.todo : 'People this rule reaches, after removals'}
                >
                  {incomplete ? 'Unfinished' : preview.loading && preview.count === null ? '…'
                    : `${countByRule[index]} ${countByRule[index] === 1 ? 'person' : 'people'}`}
                </span>
                <button
                  type="button"
                  onClick={() => setModal({ index })}
                  className="shrink-0 text-slate-400 hover:text-blue-600"
                  title="Edit this rule"
                >
                  <Pencil className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => removeRule(index)}
                  className="shrink-0 text-slate-400 hover:text-red-600"
                  title="Remove this rule"
                >
                  <X className="w-4 h-4" />
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <button
          type="button"
          onClick={() => setModal({ index: null })}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-slate-300 rounded-lg text-slate-700 bg-white hover:bg-slate-50"
        >
          <Plus className="w-4 h-4" /> {hasRules ? 'Add Rule' : 'Add Expected Attendees'}
        </button>
        <span className="text-xs text-slate-500">
          {!hasRules
            ? 'No rules yet, so nobody will be listed as expected.'
            : rules.include.length > 1
              ? 'Rules add up: anyone matching any one of them is expected.'
              : ''}
        </span>
      </div>

      {/* ── Excluded people ───────────────────────────────────────────────── */}
      {rules.exclude.staffIds.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-lg p-3">
          <p className="text-sm font-medium text-slate-800 mb-2">
            Removed ({rules.exclude.staffIds.length})
          </p>
          <div className="flex flex-wrap gap-1.5">
            {rules.exclude.staffIds.map((id) => (
              <span key={id} className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 border border-slate-200 rounded text-xs text-slate-600">
                {staffName(id)}
                <button type="button" onClick={() => unexclude(id)} className="text-slate-400 hover:text-blue-600" title="Put back">
                  <RefreshCw className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* ── Preview ───────────────────────────────────────────────────────── */}
      {hasRules && (
        <div className="bg-white border border-slate-200 rounded-lg p-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-slate-800">
              {preview.loading && preview.count === null
                ? 'Working out who…'
                : preview.error
                  ? 'Preview failed'
                  : `${preview.count ?? 0} ${preview.count === 1 ? 'person' : 'people'} expected`}
            </span>
            {preview.data.length > 0 && (
              <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                className="text-xs text-blue-600 hover:underline"
              >
                {expanded ? 'Hide List' : 'Show List'}
              </button>
            )}
          </div>

          {preview.error && (
            <p className="text-xs text-red-600 mt-1.5">{preview.error}</p>
          )}
          {trainerStaffIds.length > 0 && (
            <p className="text-xs text-slate-500 mt-1.5">
              {trainerStaffIds.map(staffName).join(', ')}
              {trainerStaffIds.length === 1 ? ' is' : ' are'} running this session, so they are left
              off. A trainer is never an expected attendee, and teaching earns trainer hours only.
            </p>
          )}
          {!preview.loading && !preview.error && preview.count === 0 && (
            <p className="text-xs text-amber-600 mt-1.5">
              These rules match nobody. The session would have an empty expected list.
            </p>
          )}

          {expanded && (
            <ul className="mt-2 divide-y divide-slate-100 max-h-60 overflow-y-auto">
              {preview.data.map((p) => (
                <li key={p.staffId} className="flex items-center gap-2 py-1.5 text-sm">
                  <span className="text-slate-700 truncate">{p.name}</span>
                  <span className="text-xs text-slate-600 truncate">
                    {[p.department, p.designation].filter(Boolean).join(' · ')}
                  </span>
                  <span className="ml-auto shrink-0 flex items-center gap-2">
                    <span className="px-1.5 py-0.5 bg-slate-100 rounded text-xs text-slate-500 truncate max-w-[14rem]" title={p.ruleLabel}>
                      {p.ruleLabel}
                    </span>
                    <button
                      type="button"
                      onClick={() => exclude(p.staffId)}
                      className="text-slate-400 hover:text-red-600"
                      title="Remove this person from the expected list"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {modal && (
        <AudienceRuleModal
          rule={modal.index === null ? null : rules.include[modal.index]}
          usedTypes={usedTypes}
          excludeStaffIds={rules.exclude.staffIds}
          trainerStaffIds={trainerStaffIds}
          lookupOptions={lookupOptions}
          staffOptions={staffOptions}
          categoryOptions={categoryOptions}
          qualTypeOptions={qualTypeOptions}
          pastSessions={pastSessions}
          fetchJSON={fetchJSON}
          onSave={saveRule}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
};

export default AudienceRulesEditor;
